import * as React from 'react';
import queryString from 'query-string';
import { ScrollContainer } from 'components/ScrollContainer';
import FilterPanel from 'components/FilterPanel';
import { ApiContext } from 'context/ApiContext';
import {
  ICurrencyFormat,
  IApplicationChartResponse,
  IApplicationSpendItem,
  IDisplayByType,
  IHeadlinesResponse,
  ISettingsResponse,
  ISelectOption,
  IFileData,
  IFileDataType,
} from 'utils/types';
import QueryParams, { getQueryDatesFromPeriod } from 'context/QueryParams';
import {
  CHART_TYPES,
  CONTRIBUTIONS,
  DISPLAY_BY,
  DISPLAY_BY_TO_API,
  PERMISSIONS,
} from 'utils/constants';
import NoData from 'views/NoData/NoData';
import NotificationBar from 'components/NotificationBar/NotificationBar';
import HeroContainer from './HeroContainer';
import { PieChartsContainer, PieChartSection } from './ApplicationChart.styles';
import { SecondaryContent } from './Applications.styles';
import { TopApplicationsContainer } from './TopApplications/TopApplicationsContainer';
import { SplitApplicationsContainer } from './SplitApplications/SplitApplicationsContainer';
import ApplicationsData from './ApplicationsContext';
import {
  exportApplicationContributionData,
  exportApplicationHeroData,
  exportApplicationPieChartData,
  exportApplicationSummaryData,
  fetchFiles,
  fetchHeadlines,
  getApplicationCharts,
} from 'utils/api';
import isEqual from 'lodash/isEqual';
import { getLocalStorageItem, setLocalStorageItem } from 'utils/localStorage';
import { NoData as NoApplications } from 'components/SpendAllocation/NoData/NoData';
import { ApplicationsTableContainer } from './ApplicationsTableContainer';
import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';

const FILTERS_LOCAL_STORAGE_KEY = 'TXR_APPLICATIONS_ANALYSIS_FILTERS_KEY';
const LOCAL_STORAGE_KEY = 'TXR_APPLICATIONS_ANALYSIS_STATE_KEY';

interface IApplicationsViewProps {
  format: ICurrencyFormat;
  params: { [key: string]: string | string[] };
  setParams: (p: any) => void;
  settings: ISettingsResponse;
  permissions: string[];
}

interface IApplicationsConfigState {
  chartOption: ISelectOption;
  contributions?: string;
  displayBy: IDisplayByType;
  selectedGroup?: string;
  selectedApplication?: string;
}

interface IApplicationsState extends IApplicationsConfigState {
  allApplications: IApplicationSpendItem[];
  applications: IApplicationSpendItem[];
  contributions?: string;
  displayBy: IDisplayByType;
  headlines?: IHeadlinesResponse;
  isApplicationsExist: boolean;
  isSuccessNotification: boolean;
  loading: boolean;
  selectedGroup?: string;
  selectedApplication?: string;
}

const EMPTY_APP = {
  applicationName: '',
  applicationType: '',
  businessOwner: '',
  contribution: '',
  groupName: '',
  spend: 0,
  headcount: 0,
  fte: 0,
};

class ApplicationsView extends React.Component<
  IApplicationsViewProps,
  IApplicationsState
> {
  state = {
    allApplications: [],
    applications: [],
    headlines: undefined,
    isApplicationsExist: true,
    isSuccessNotification: false,
    loading: true,
    ...this.getDefaultConfig(),
  };

  getDefaultConfig() {
    const localStorageState = getLocalStorageItem(LOCAL_STORAGE_KEY);
    const defaultState: IApplicationsConfigState = {
      chartOption: CHART_TYPES[1],
      contributions: undefined,
      displayBy: IDisplayByType.COST,
      selectedGroup: undefined,
      selectedApplication: undefined,
    };

    return {
      ...defaultState,
      ...localStorageState,
    } as IApplicationsConfigState;
  }

  setConfigToLocalStorage = () => {
    const {
      chartOption,
      contributions,
      displayBy,
      selectedGroup,
      selectedApplication,
    } = this.state;
    setLocalStorageItem(LOCAL_STORAGE_KEY, {
      chartOption,
      contributions,
      displayBy,
      selectedGroup,
      selectedApplication,
    });
  };

  showSuccessNotification = () => {
    this.setState({ isSuccessNotification: true });
    setTimeout(() => {
      this.setState({ isSuccessNotification: false });
    }, 5000);
  };

  isPersonnelBecomeSelected = (prevState: IApplicationsState) => {
    const { contributions } = this.state;
    return (
      contributions === CONTRIBUTIONS.PERSONNEL &&
      prevState.contributions !== CONTRIBUTIONS.PERSONNEL
    );
  };

  isPersonnelBecomeUnSelected = (prevState: IApplicationsState) => {
    const { contributions } = this.state;
    return (
      contributions !== CONTRIBUTIONS.PERSONNEL &&
      prevState.contributions === CONTRIBUTIONS.PERSONNEL
    );
  };

  isSettingsAvailable = () => {
    const { settings } = this.props;
    return (
      settings &&
      settings.availableRange &&
      settings.availableRange.startYear &&
      settings.availableRange.endYear
    );
  };

  updateDisplayBy = (displayBy: IDisplayByType) => {
    this.setState({ displayBy });
  };

  onContributionChange = (contributions: string) => {
    this.setState({ contributions });
  };

  componentDidMount() {
    if (this.isSettingsAvailable()) {
      this.getApplicationData();
      this.getAllApplicationsData();
    }
    this.getFilesData();
  }
  getFilesData = () => {
    fetchFiles().then((files: IFileData[]) => {
      const isApplicationsExist = files.some(
        (file) => file.fileDataType === IFileDataType.APPLICATIONS
      );
      this.setState({ isApplicationsExist });
    });
  };

  getApplicationData = () => {
    this.setState({ loading: true });
    getApplicationCharts(this.getQueryParams()).then(
      (resp: IApplicationChartResponse) => {
        this.setState({
          applications: resp.applicationsSpend,
          loading: false,
        });
      }
    );
  };

  getAllApplicationsData = () => {
    const { settings } = this.props;
    if (settings && settings.selectedYear) {
      const { startDate, endDate } = getQueryDatesFromPeriod(
        settings.selectedYear
      );

      const searchQuery = queryString.stringify({ startDate, endDate });
      getApplicationCharts(searchQuery).then(
        (resp: IApplicationChartResponse) => {
          this.setState({
            allApplications: resp.applicationsSpend,
          });
        }
      );
    }
  };

  downloadApplicationHeroData = () =>
    exportApplicationHeroData(this.getQueryParams());

  downloadApplicationContributionData = () =>
    exportApplicationContributionData(this.getQueryParams(true));

  downloadApplicationPieData = () =>
    exportApplicationPieChartData(this.getQueryParams());

  downloadApplicationSummaryData = () =>
    exportApplicationSummaryData(this.getQueryParams(true));

  handleExportToImage = (chartInstance: any, filename: string) => {
    if (chartInstance) {
      chartInstance.exportChart({ filename }, { chart: { width: 1200 } });
    }
  };

  // Method to fetch data with standard query params
  getQueryParams = (isAppName = false) => {
    const { contributions, displayBy, selectedGroup, selectedApplication } =
      this.state;
    const { params, settings } = this.props;
    // @ts-ignore
    if (settings && settings?.selectedYear) {
      const { startDate, endDate } = getQueryDatesFromPeriod(
        // @ts-ignore
        settings.selectedYear
      );

      const searchQuery = queryString.stringify({ startDate, endDate });
      let spendViewTypeQuery = '';
      let filtersQuery = '';
      let selectedGroupQuery = '';
      let selectedApplicationQuery = '';
      let contributionsQuery = '';
      if (Object.keys(params).length !== 0) {
        // Remove empty parameters from query
        const query = Object.fromEntries(
          Object.entries(params).filter(([_, v]) => v != null)
        );
        filtersQuery = `&${queryString.stringify(query, {
          arrayFormat: 'index',
        })}`;
      }
      if (displayBy) {
        spendViewTypeQuery = `&spendViewType=${DISPLAY_BY_TO_API[displayBy]}`;
      }
      if (selectedGroup) {
        selectedGroupQuery = `&groupName=${selectedGroup}`;
      }
      if (selectedApplication && isAppName) {
        selectedApplicationQuery = `&appName=${selectedApplication}`;
      }
      if (contributions) {
        contributionsQuery = `&contributions=${contributions}`;
      }
      return `${searchQuery}${filtersQuery}${spendViewTypeQuery}${selectedGroupQuery}${selectedApplicationQuery}${contributionsQuery}`;
    }
    return '';
  };

  fetchHeadlines = () => {
    return fetchHeadlines(this.getQueryParams()).then(
      (headlinesResponse: IHeadlinesResponse) => {
        this.setState({ headlines: headlinesResponse });
      }
    );
  };

  componentDidUpdate(
    prevProps: IApplicationsViewProps,
    prevState: IApplicationsState
  ) {
    const { permissions } = this.props;
    const { contributions } = this.state;

    if (this.isPersonnelBecomeUnSelected(prevState)) {
      this.setState({ displayBy: IDisplayByType.COST });
    }
    if (
      !permissions.includes(PERMISSIONS.APPLICATIONS_PERSONNEL) &&
      contributions === CONTRIBUTIONS.PERSONNEL
    ) {
      this.setState({ contributions: CONTRIBUTIONS.THIRD_PARTY });
      return;
    }
    if (!isEqual(this.state, prevState)) {
      this.setConfigToLocalStorage();
    }
    if (!isEqual(this.props.settings, prevProps.settings)) {
      this.getAllApplicationsData();
    }
    if (
      !isEqual(prevProps.params, this.props.params) ||
      !isEqual(this.props.settings, prevProps.settings) ||
      contributions !== prevState.contributions
    ) {
      this.getApplicationData();
    }
  }

  render() {
    const { format, permissions, settings } = this.props;
    const {
      allApplications,
      applications,
      chartOption,
      displayBy,
      contributions,
      isApplicationsExist,
      isSuccessNotification,
      loading,
      selectedGroup,
      selectedApplication,
    } = this.state;
    if (!isApplicationsExist) {
      return <NoApplications />;
    }
    if (!settings) {
      return null;
    }
    const filteredApps = applications.filter(
      (app: IApplicationSpendItem) =>
        !selectedGroup || app.groupName === selectedGroup
    );
    const isBothContributions =
      contributions !== CONTRIBUTIONS.PERSONNEL &&
      contributions !== CONTRIBUTIONS.THIRD_PARTY;
    const isPersonnel = contributions === CONTRIBUTIONS.PERSONNEL;
    const orderProp =
      displayBy === DISPLAY_BY.HEADCOUNT ? 'headcount' : 'spend';
    let filteredApplications = orderBy(filteredApps, orderProp, 'desc');
    if (!isBothContributions) {
      filteredApplications = filteredApplications.filter(
        (application: IApplicationSpendItem) =>
          isPersonnel
            ? application.contribution === 'Personnel'
            : application.contribution === 'ThirdParty'
      );
    }
    const groupedApps = groupBy(filteredApplications, 'applicationName');
    const aggregatedApps: IApplicationSpendItem[] = Object.keys(
      groupedApps
    ).map((appName: string) => {
      return groupedApps[appName].reduce((acc, app: IApplicationSpendItem) => {
        return {
          ...app,
          spend: acc.spend + app.spend,
          headcount: acc.headcount + app.headcount,
          fte: acc.fte + app.fte,
        };
      }, EMPTY_APP);
    });
    return this.isSettingsAvailable() ? (
      <>
        <FilterPanel localStorageKey={FILTERS_LOCAL_STORAGE_KEY} />
        <ScrollContainer>
          <HeroContainer
            applications={filteredApplications}
            chartOption={chartOption}
            contributions={contributions}
            displayBy={displayBy}
            downloadApplicationHeroData={this.downloadApplicationHeroData}
            loading={loading}
            handleExportToImage={this.handleExportToImage}
            updateDisplayBy={this.updateDisplayBy}
            onContributionChange={this.onContributionChange}
            selectedGroup={selectedGroup}
            selectedApplication={selectedApplication}
            updateAppState={(st: any) => {
              this.setState(st);
            }}
            showSuccessNotification={this.showSuccessNotification}
          />
          <SecondaryContent>
            <PieChartsContainer>
              <PieChartSection>
                <TopApplicationsContainer
                  applications={filteredApplications}
                  displayBy={displayBy}
                  downloadApplicationPieData={this.downloadApplicationPieData}
                  format={format}
                  selectedGroup={selectedGroup}
                  selectedApplication={selectedApplication}
                  showSuccessNotification={this.showSuccessNotification}
                  updateAppState={(st: any) => {
                    this.setState(st);
                  }}
                />
              </PieChartSection>
              <PieChartSection>
                <SplitApplicationsContainer
                  applications={allApplications}
                  displayBy={displayBy}
                  downloadApplicationContributionData={
                    this.downloadApplicationContributionData
                  }
                  format={format}
                  permissions={permissions}
                  selectedGroup={selectedGroup}
                  selectedApplication={selectedApplication}
                  showSuccessNotification={this.showSuccessNotification}
                />
              </PieChartSection>
            </PieChartsContainer>
            <ApplicationsTableContainer
              applications={aggregatedApps}
              displayBy={displayBy}
              format={format}
              selectedGroup={selectedGroup}
              selectedApplication={selectedApplication}
              downloadApplicationSummaryData={
                this.downloadApplicationSummaryData
              }
              showSuccessNotification={this.showSuccessNotification}
            />
          </SecondaryContent>
          {isSuccessNotification && (
            <NotificationBar successMessage="Generating file... You will receive a notification when the file has been generated" />
          )}
        </ScrollContainer>
      </>
    ) : (
      <NoData />
    );
  }
}

export const Applications = (props: any) => (
  <QueryParams>
    {({ params, setParams }) => (
      <ApiContext.Consumer>
        {({ format, permissions, settings }) => (
          <ApplicationsData>
            <ApplicationsView
              {...props}
              format={format}
              params={params}
              permissions={permissions}
              setParams={setParams}
              settings={settings}
            />
          </ApplicationsData>
        )}
      </ApiContext.Consumer>
    )}
  </QueryParams>
);
